import { z } from 'zod';
import type { Nodo, TipoNodo } from './tipos.js';

/**
 * Enlaces compartidos.
 *
 * Un enlace expone exactamente un nodo: si es un archivo, ese archivo; si es
 * una carpeta, esa carpeta y lo que contiene. Nunca se puede subir de ahí
 * hacia el resto del repositorio — para quien recibe el enlace, el nodo
 * compartido ES la raíz del mundo.
 */

export interface Enlace {
  id: string;
  nodoId: string;
  nodoNombre: string;
  nodoTipo: TipoNodo;
  /** URL completa, lista para copiar. Se emite al vuelo: no se guarda en la base. */
  url: string;
  permiteDescarga: boolean;
  expiraEn: string | null;
  revocadoEn: string | null;
  nota: string | null;
  visitas: number;
  ultimaVisita: string | null;
  creadoPor: string | null;
  creadoPorNombre: string | null;
  creadoEn: string;
  /** Derivado: caducado o revocado. */
  activo: boolean;
}

/** Lo que ve quien abre un enlace, sin haber iniciado sesión. */
export interface ContenidoCompartido {
  enlace: {
    permiteDescarga: boolean;
    expiraEn: string | null;
    compartidoPor: string | null;
    organizacion: string;
  };
  /** El nodo compartido: la raíz de lo que puede ver. */
  raiz: NodoCompartido;
  /** La carpeta que está mirando ahora (la raíz misma, o una subcarpeta). */
  carpeta: NodoCompartido | null;
  /** Migas desde la raíz compartida hacia abajo. Nunca incluye ancestros de fuera. */
  ruta: Array<{ id: string; nombre: string }>;
  hijos: NodoCompartido[];
}

export type NodoCompartido = Pick<
  Nodo,
  'id' | 'tipo' | 'nombre' | 'mime' | 'bytes' | 'version' | 'extension' | 'actualizadoEn'
>;

export const DIAS_VIGENCIA = [7, 30, 90, 365] as const;

export const esquemaCrearEnlace = z.object({
  /** null = sin caducidad. La interfaz avisa de lo que eso significa. */
  diasVigencia: z.number().int().min(1).max(3650).nullable().default(30),
  permiteDescarga: z.boolean().default(true),
  nota: z.string().trim().max(200).nullable().optional(),
});
